import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Shield, Users, Building2, Activity, LogOut, Plus, Search,
  MoreVertical, Ban, CheckCircle, Trash2, Key, Crown, Eye,
  Clock, FileText, TrendingUp, AlertTriangle, CreditCard, Edit, 
  Sparkles, Bell, IndianRupee, Calendar, Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Indian date formatting
const formatDateIndian = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
};

const subscriptionColors = {
  basic: 'bg-slate-100 text-slate-700',
  pro: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700',
  complimentary: 'bg-green-100 text-green-700'
};

const statusColors = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  deleted: 'bg-gray-100 text-gray-700'
};

function MasterAdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  
  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    pump_name: '',
    owner_name: '',
    dealership: 'BPCL',
    location: '',
    town: '',
    district: '',
    state: '',
    mobile: '',
    email: '',
    hsd_dispensers: 2,
    ms_dispensers: 2,
    hsd_tanks: 1,
    ms_tanks: 1,
    hsd_tank_capacity: 10000,
    ms_tank_capacity: 10000,
    subscription_level: 'basic'
  });

  // Subscription Plans Management State
  const [subscriptionConfig, setSubscriptionConfig] = useState(null);
  const [planDialog, setPlanDialog] = useState(false);
  const [offerDialog, setOfferDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    duration_months: 1,
    features: [''],
    is_active: true,
    is_popular: false
  });
  const [offerForm, setOfferForm] = useState({
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    valid_from: '',
    valid_until: '',
    applicable_plans: [],
    is_active: true
  });
  const [subscriptionStatusList, setSubscriptionStatusList] = useState([]);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('masterAdminToken');
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    const storedAdmin = localStorage.getItem('masterAdmin');
    if (!storedAdmin) {
      navigate('/master-admin/login');
      return;
    }
    setAdmin(JSON.parse(storedAdmin));
    fetchDashboard();
    fetchCustomers();
    fetchAuditLogs();
    fetchSubscriptionConfig();
    fetchSubscriptionStatus();
  }, [navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/master-admin/dashboard`, { headers: getAuthHeader() });
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (subscriptionFilter && subscriptionFilter !== 'all') params.append('subscription', subscriptionFilter);
      
      const response = await axios.get(`${API}/api/master-admin/customers?${params}`, { headers: getAuthHeader() });
      setCustomers(response.data.customers);
      setTotalCustomers(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/api/master-admin/audit-logs?limit=20`, { headers: getAuthHeader() });
      setAuditLogs(response.data.logs);
    } catch (error) {
      console.error('Failed to fetch audit logs');
    }
  };

  const fetchSubscriptionConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/master-admin/subscription-config`, { headers: getAuthHeader() });
      setSubscriptionConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription config');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/master-admin/subscription-status`, { headers: getAuthHeader() });
      setSubscriptionStatusList(response.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch subscription status');
    }
  };

  // Subscription Plan CRUD
  const handleSavePlan = async (e) => {
    e.preventDefault();
    try {
      const planData = {
        ...planForm,
        price: parseFloat(planForm.price),
        features: planForm.features.filter(f => f.trim() !== '')
      };

      if (editingPlan) {
        await axios.put(
          `${API}/api/master-admin/subscription-plans/${editingPlan.id}`,
          planData,
          { headers: getAuthHeader() }
        );
        toast.success('Plan updated successfully');
      } else {
        await axios.post(
          `${API}/api/master-admin/subscription-plans`,
          planData,
          { headers: getAuthHeader() }
        );
        toast.success('Plan created successfully');
      }
      
      setPlanDialog(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchSubscriptionConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    try {
      await axios.delete(
        `${API}/api/master-admin/subscription-plans/${planId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Plan deleted');
      fetchSubscriptionConfig();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price?.toString() || '',
      duration_months: plan.duration_months || 1,
      features: plan.features?.length > 0 ? plan.features : [''],
      is_active: plan.is_active !== false,
      is_popular: plan.is_popular || false
    });
    setPlanDialog(true);
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      description: '',
      price: '',
      duration_months: 1,
      features: [''],
      is_active: true,
      is_popular: false
    });
  };

  // Subscription Offers CRUD
  const handleSaveOffer = async (e) => {
    e.preventDefault();
    try {
      const offerData = {
        ...offerForm,
        discount_value: parseFloat(offerForm.discount_value)
      };
      
      await axios.post(
        `${API}/api/master-admin/subscription-offers`,
        offerData,
        { headers: getAuthHeader() }
      );
      toast.success('Offer created successfully');
      setOfferDialog(false);
      resetOfferForm();
      fetchSubscriptionConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save offer');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    try {
      await axios.delete(
        `${API}/api/master-admin/subscription-offers/${offerId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Offer deleted');
      fetchSubscriptionConfig();
    } catch (error) {
      toast.error('Failed to delete offer');
    }
  };

  const resetOfferForm = () => {
    setOfferForm({
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      valid_from: '',
      valid_until: '',
      applicable_plans: [],
      is_active: true
    });
  };

  // Subscription Alerts Update
  const handleUpdateAlerts = async (alerts) => {
    try {
      await axios.put(
        `${API}/api/master-admin/subscription-alerts`,
        alerts,
        { headers: getAuthHeader() }
      );
      toast.success('Alerts updated successfully');
      fetchSubscriptionConfig();
    } catch (error) {
      toast.error('Failed to update alerts');
    }
  };

  // Update Grace Period
  const handleUpdateGracePeriod = async (days) => {
    try {
      await axios.put(
        `${API}/api/master-admin/subscription-config`,
        { ...subscriptionConfig, grace_period_days: parseInt(days) },
        { headers: getAuthHeader() }
      );
      toast.success('Grace period updated');
      fetchSubscriptionConfig();
    } catch (error) {
      toast.error('Failed to update grace period');
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    try {
      const response = await axios.get(`${API}/api/master-admin/customers/${customerId}`, { headers: getAuthHeader() });
      setCustomerDetail(response.data);
      setDetailDialog(true);
    } catch (error) {
      toast.error('Failed to fetch customer details');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/api/master-admin/customers?subscription_level=${createForm.subscription_level}`,
        createForm,
        { headers: getAuthHeader() }
      );
      toast.success(`Customer created! Temp PIN: ${response.data.temp_pin}`);
      setCreateDialog(false);
      fetchCustomers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  const handleSuspend = async (customerId) => {
    if (!window.confirm('Are you sure you want to suspend this customer?')) return;
    try {
      await axios.put(`${API}/api/master-admin/customers/${customerId}/suspend`, {}, { headers: getAuthHeader() });
      toast.success('Customer suspended');
      fetchCustomers();
      fetchDashboard();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend');
    }
  };

  const handleActivate = async (customerId) => {
    try {
      await axios.put(`${API}/api/master-admin/customers/${customerId}/activate`, {}, { headers: getAuthHeader() });
      toast.success('Customer activated');
      fetchCustomers();
      fetchDashboard();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/master-admin/customers/${customerId}`, { headers: getAuthHeader() });
      toast.success('Customer deleted');
      fetchCustomers();
      fetchDashboard();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleResetPassword = async (customerId) => {
    try {
      const response = await axios.put(`${API}/api/master-admin/customers/${customerId}/reset-password`, {}, { headers: getAuthHeader() });
      toast.success(`Password reset! Temp PIN: ${response.data.temp_pin}`);
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleUpdateSubscription = async (customerId, level, complimentary = false) => {
    try {
      await axios.put(
        `${API}/api/master-admin/customers/${customerId}/subscription?subscription_level=${level}&is_complimentary=${complimentary}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Subscription updated');
      fetchCustomers();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleLoginAsCustomer = async (customerId) => {
    try {
      const response = await axios.post(`${API}/api/master-admin/login-as-customer/${customerId}`, {}, { headers: getAuthHeader() });
      // Store token and redirect
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify({ pump_id: customerId, role: 'admin' }));
      window.open('/', '_blank');
      toast.success('Logged in as customer in new tab');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as customer');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdmin');
    navigate('/master-admin/login');
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, statusFilter, subscriptionFilter]);

  const dealerships = ['BPCL', 'HPCL', 'IOCL', 'Jio-BP', 'Nayara', 'Reliance Petroleum', 'Shell'];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="master-admin-dashboard">
      {/* Top Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src="https://customer-assets.emergentagent.com/job_f33e49fe-e820-4c95-aab4-40ccc87efe18/artifacts/dtk4fxv6_Fuelogic%20White%20Background%20Logo%20dark%20greay.jpg" alt="Fuelogic" className="w-12 h-12 object-contain rounded-lg" />
            <div>
              <h1 className="font-heading font-bold text-xl text-white tracking-tight">Solution Provider Portal</h1>
              <p className="text-xs text-slate-400">Manage customers & subscriptions</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-300">Welcome, {admin?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Total Customers</p>
                  <p className="text-xl font-bold text-white">{dashboard.overview.total_customers}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Active</p>
                  <p className="text-xl font-bold text-white">{dashboard.overview.active_customers}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Ban className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Suspended</p>
                  <p className="text-xl font-bold text-white">{dashboard.overview.suspended_customers}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">New This Month</p>
                  <p className="text-xl font-bold text-white">{dashboard.overview.new_this_month}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Activity className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Active (30d)</p>
                  <p className="text-xl font-bold text-white">{dashboard.overview.active_last_30_days}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/20">
                  <Users className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Total Users</p>
                  <p className="text-xl font-bold text-white">{dashboard.usage.total_staff_accounts}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Subscription Breakdown */}
        {dashboard && (
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 bg-slate-800 border-slate-700">
              <p className="text-xs text-slate-400 mb-1">Basic</p>
              <p className="text-2xl font-bold text-slate-300">{dashboard.subscriptions.basic}</p>
            </Card>
            <Card className="p-4 bg-slate-800 border-blue-500/30 border-2">
              <p className="text-xs text-blue-400 mb-1">Pro</p>
              <p className="text-2xl font-bold text-blue-400">{dashboard.subscriptions.pro}</p>
            </Card>
            <Card className="p-4 bg-slate-800 border-purple-500/30 border-2">
              <p className="text-xs text-purple-400 mb-1">Enterprise</p>
              <p className="text-2xl font-bold text-purple-400">{dashboard.subscriptions.enterprise}</p>
            </Card>
            <Card className="p-4 bg-slate-800 border-green-500/30 border-2">
              <p className="text-xs text-green-400 mb-1">Complimentary</p>
              <p className="text-2xl font-bold text-green-400">{dashboard.subscriptions.complimentary}</p>
            </Card>
          </div>
        )}

        <Tabs defaultValue="customers" className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="customers" className="data-[state=active]:bg-slate-700">
              <Building2 className="w-4 h-4 mr-2" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-slate-700">
              <CreditCard className="w-4 h-4 mr-2" />
              Subscription Plans
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-slate-700">
              <FileText className="w-4 h-4 mr-2" />
              Audit Logs
            </TabsTrigger>
          </TabsList>

          {/* CUSTOMERS TAB */}
          <TabsContent value="customers" className="mt-6">
            {/* Filters & Actions */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="customer-search"
                  placeholder="Search by name, mobile, email, town..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                </SelectContent>
              </Select>
              <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="All Plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="complimentary">Complimentary</SelectItem>
                </SelectContent>
              </Select>
              <Dialog open={createDialog} onOpenChange={setCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500" data-testid="create-customer-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Customer
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle>Create New Customer</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCustomer} className="space-y-4 mt-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">Pump Name *</label>
                        <Input 
                          value={createForm.pump_name} 
                          onChange={(e) => setCreateForm({...createForm, pump_name: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">Owner Name *</label>
                        <Input 
                          value={createForm.owner_name} 
                          onChange={(e) => setCreateForm({...createForm, owner_name: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">Dealership *</label>
                        <Select value={createForm.dealership} onValueChange={(v) => setCreateForm({...createForm, dealership: v})}>
                          <SelectTrigger className="bg-slate-700 border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {dealerships.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">Subscription *</label>
                        <Select value={createForm.subscription_level} onValueChange={(v) => setCreateForm({...createForm, subscription_level: v})}>
                          <SelectTrigger className="bg-slate-700 border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                            <SelectItem value="complimentary">Complimentary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">Mobile *</label>
                        <Input 
                          value={createForm.mobile} 
                          onChange={(e) => setCreateForm({...createForm, mobile: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">Email *</label>
                        <Input 
                          type="email"
                          value={createForm.email} 
                          onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2 text-slate-300">Location *</label>
                        <Input 
                          value={createForm.location} 
                          onChange={(e) => setCreateForm({...createForm, location: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">Town *</label>
                        <Input 
                          value={createForm.town} 
                          onChange={(e) => setCreateForm({...createForm, town: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">District *</label>
                        <Input 
                          value={createForm.district} 
                          onChange={(e) => setCreateForm({...createForm, district: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">State *</label>
                        <Input 
                          value={createForm.state} 
                          onChange={(e) => setCreateForm({...createForm, state: e.target.value})}
                          className="bg-slate-700 border-slate-600"
                          required 
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
                      <Button type="submit" className="bg-gradient-to-r from-amber-500 to-orange-500">Create Customer</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Customers Table */}
            <Card className="bg-slate-800 border-slate-700 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Pump / Owner</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Dealership</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Subscription</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Last Login</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Usage</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-slate-700/50" data-testid={`customer-row-${customer.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-white">{customer.pump_name}</p>
                        <p className="text-xs text-slate-400">{customer.owner_name}</p>
                        <p className="text-xs text-slate-500">{customer.mobile}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">{customer.dealership}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-300">{customer.town}</p>
                        <p className="text-xs text-slate-500">{customer.district}, {customer.state}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${subscriptionColors[customer.subscription_level || 'basic']}`}>
                          {customer.subscription_level || 'basic'}
                        </span>
                        {customer.is_complimentary && (
                          <span className="ml-1 text-xs text-green-400">★</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColors[customer.account_status || 'active']}`}>
                          {customer.account_status || 'active'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400">
                        {customer.last_login ? new Date(customer.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-400">
                          <p>{customer.stats?.users_count || 0} users</p>
                          <p>{customer.stats?.transactions_count || 0} trans</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-slate-400 hover:text-white"
                            onClick={() => fetchCustomerDetail(customer.id)}
                            data-testid={`view-customer-${customer.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-slate-400 hover:text-blue-400"
                            onClick={() => handleLoginAsCustomer(customer.id)}
                          >
                            <LogOut className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-slate-400 hover:text-amber-400"
                            onClick={() => handleResetPassword(customer.id)}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          {customer.account_status === 'active' ? (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-slate-400 hover:text-red-400"
                              onClick={() => handleSuspend(customer.id)}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-slate-400 hover:text-green-400"
                              onClick={() => handleActivate(customer.id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-slate-400 hover:text-red-500"
                            onClick={() => handleDelete(customer.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {customers.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No customers found</p>
                </div>
              )}
            </Card>
            <p className="text-sm text-slate-400 mt-4">Showing {customers.length} of {totalCustomers} customers</p>
          </TabsContent>

          {/* AUDIT LOGS TAB */}
          <TabsContent value="audit" className="mt-6">
            <Card className="bg-slate-800 border-slate-700 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Admin</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-sm text-slate-400">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">{log.admin_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          log.action.includes('DELETE') ? 'bg-red-500/20 text-red-400' :
                          log.action.includes('SUSPEND') ? 'bg-orange-500/20 text-orange-400' :
                          log.action.includes('CREATE') ? 'bg-green-500/20 text-green-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {log.target_name || log.target_id}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {log.details ? JSON.stringify(log.details) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* SUBSCRIPTION PLANS TAB */}
          <TabsContent value="subscriptions" className="mt-6 space-y-6">
            {/* Subscription Config Summary */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <CreditCard className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Active Plans</p>
                    <p className="text-xl font-bold text-white">
                      {subscriptionConfig?.plans?.filter(p => p.is_active !== false).length || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <Sparkles className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Active Offers</p>
                    <p className="text-xl font-bold text-white">
                      {subscriptionConfig?.offers?.filter(o => o.is_active).length || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/20">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Expired Subscriptions</p>
                    <p className="text-xl font-bold text-white">
                      {subscriptionStatusList.filter(s => s.subscription_status === 'expired').length}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Grace Period</p>
                    <p className="text-xl font-bold text-white">
                      {subscriptionConfig?.grace_period_days || 7} days
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Plans Section */}
            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-400" />
                  Subscription Plans
                </h3>
                <Button 
                  onClick={() => { resetPlanForm(); setEditingPlan(null); setPlanDialog(true); }}
                  className="bg-gradient-to-r from-amber-500 to-orange-500"
                  data-testid="add-plan-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Plan
                </Button>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                {subscriptionConfig?.plans?.map((plan) => (
                  <Card key={plan.id} className={`p-4 bg-slate-700 border-slate-600 relative ${plan.is_popular ? 'border-amber-400 border-2' : ''}`}>
                    {plan.is_popular && (
                      <span className="absolute -top-2 left-4 px-2 py-0.5 bg-amber-400 text-amber-900 text-xs font-bold rounded">
                        POPULAR
                      </span>
                    )}
                    <div className="flex justify-between items-start mt-2">
                      <div>
                        <h4 className="font-semibold text-white">{plan.name}</h4>
                        <p className="text-sm text-slate-400">{plan.description}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEditPlan(plan)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeletePlan(plan.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-amber-400 mt-3">
                      ₹{plan.price?.toLocaleString('en-IN')}
                      <span className="text-sm font-normal text-slate-400">
                        /{plan.duration_months === 1 ? 'mo' : `${plan.duration_months}mo`}
                      </span>
                    </p>
                    <ul className="mt-3 space-y-1">
                      {plan.features?.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="text-xs text-slate-300 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 pt-3 border-t border-slate-600">
                      <span className={`px-2 py-1 rounded text-xs ${plan.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {plan.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </Card>
                ))}
                {(!subscriptionConfig?.plans || subscriptionConfig.plans.length === 0) && (
                  <div className="col-span-3 text-center py-8 text-slate-400">
                    No subscription plans configured. Click "Add Plan" to create one.
                  </div>
                )}
              </div>
            </Card>

            {/* Offers Section */}
            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-green-400" />
                  Special Offers
                </h3>
                <Button 
                  onClick={() => { resetOfferForm(); setOfferDialog(true); }}
                  variant="outline"
                  data-testid="add-offer-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Offer
                </Button>
              </div>
              
              <div className="space-y-3">
                {subscriptionConfig?.offers?.map((offer) => (
                  <div key={offer.id} className="flex justify-between items-center p-4 bg-slate-700 rounded-lg">
                    <div>
                      <h4 className="font-medium text-white">{offer.name}</h4>
                      <p className="text-sm text-slate-400">{offer.description}</p>
                      <div className="flex gap-3 mt-2 text-xs">
                        <span className="text-green-400">
                          {offer.discount_type === 'percentage' ? `${offer.discount_value}% OFF` : `₹${offer.discount_value} OFF`}
                        </span>
                        {offer.valid_until && (
                          <span className="text-slate-400">
                            Valid until: {formatDateIndian(offer.valid_until)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${offer.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteOffer(offer.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {(!subscriptionConfig?.offers || subscriptionConfig.offers.length === 0) && (
                  <div className="text-center py-6 text-slate-400">
                    No offers configured. Click "Add Offer" to create promotional discounts.
                  </div>
                )}
              </div>
            </Card>

            {/* Alerts Configuration */}
            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-400" />
                  Subscription Alerts
                </h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-700 rounded-lg">
                  <Settings className="w-5 h-5 text-slate-400" />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-white">Grace Period (days after expiry)</label>
                    <p className="text-xs text-slate-400">Users can still access (limited) during this period</p>
                  </div>
                  <Input 
                    type="number"
                    value={subscriptionConfig?.grace_period_days || 7}
                    onChange={(e) => handleUpdateGracePeriod(e.target.value)}
                    className="w-24 bg-slate-600 border-slate-500 text-white"
                    min={0}
                    max={30}
                  />
                </div>
                
                <h4 className="text-sm font-medium text-slate-300 mt-4">Alert Messages</h4>
                {subscriptionConfig?.alerts?.map((alert, idx) => (
                  <div key={alert.id || idx} className="flex items-center gap-4 p-3 bg-slate-700 rounded-lg">
                    <span className={`w-2 h-2 rounded-full ${alert.priority === 'high' ? 'bg-red-400' : 'bg-amber-400'}`}></span>
                    <div className="flex-1">
                      <p className="text-sm text-white">{alert.alert_text}</p>
                      <p className="text-xs text-slate-400">Triggers {alert.days_before_expiry} days before expiry</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${alert.is_active ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                      {alert.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Subscription Status Overview */}
            <Card className="p-6 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-purple-400" />
                Customer Subscription Status
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900 border-b border-slate-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Expires</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Days Left</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {subscriptionStatusList.slice(0, 10).map((customer) => (
                      <tr key={customer.id} className="hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-white">{customer.pump_name}</p>
                          <p className="text-xs text-slate-400">{customer.owner_name}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${subscriptionColors[customer.subscription_level || 'basic']}`}>
                            {customer.subscription_level || 'basic'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {formatDateIndian(customer.subscription_end)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`font-medium ${
                            customer.days_remaining < 0 ? 'text-red-400' :
                            customer.days_remaining <= 7 ? 'text-amber-400' :
                            'text-green-400'
                          }`}>
                            {customer.days_remaining < 0 ? `${Math.abs(customer.days_remaining)}d overdue` : `${customer.days_remaining}d`}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            customer.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
                            customer.subscription_status === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                            customer.subscription_status === 'critical' ? 'bg-orange-500/20 text-orange-400' :
                            customer.subscription_status === 'expired' ? 'bg-red-500/20 text-red-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {customer.subscription_status || 'unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Customer Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Customer Details</DialogTitle>
            </DialogHeader>
            {customerDetail && (
              <div className="space-y-6 mt-4">
                {/* Customer Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400">Pump Name</p>
                    <p className="font-medium">{customerDetail.customer.pump_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Owner</p>
                    <p className="font-medium">{customerDetail.customer.owner_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Dealership</p>
                    <p>{customerDetail.customer.dealership}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Location</p>
                    <p>{customerDetail.customer.town}, {customerDetail.customer.district}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Mobile</p>
                    <p>{customerDetail.customer.mobile}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Email</p>
                    <p>{customerDetail.customer.email}</p>
                  </div>
                </div>

                {/* Subscription */}
                <Card className="p-4 bg-slate-700 border-slate-600">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-slate-400">Subscription</p>
                      <span className={`px-2 py-1 rounded text-sm font-semibold ${subscriptionColors[customerDetail.customer.subscription_level || 'basic']}`}>
                        {customerDetail.customer.subscription_level || 'basic'}
                      </span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="text-slate-400" onClick={() => handleUpdateSubscription(customerDetail.customer.id, 'basic')}>Basic</Button>
                      <Button size="sm" variant="outline" className="text-blue-400" onClick={() => handleUpdateSubscription(customerDetail.customer.id, 'pro')}>Pro</Button>
                      <Button size="sm" variant="outline" className="text-purple-400" onClick={() => handleUpdateSubscription(customerDetail.customer.id, 'enterprise')}>Enterprise</Button>
                      <Button size="sm" variant="outline" className="text-green-400" onClick={() => handleUpdateSubscription(customerDetail.customer.id, 'complimentary', true)}>
                        <Crown className="w-4 h-4 mr-1" />
                        Complimentary
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Stats */}
                <div className="grid grid-cols-5 gap-4">
                  <Card className="p-3 bg-slate-700 border-slate-600 text-center">
                    <p className="text-2xl font-bold">{customerDetail.stats.users_count}</p>
                    <p className="text-xs text-slate-400">Users</p>
                  </Card>
                  <Card className="p-3 bg-slate-700 border-slate-600 text-center">
                    <p className="text-2xl font-bold">{customerDetail.stats.transactions_count}</p>
                    <p className="text-xs text-slate-400">Transactions</p>
                  </Card>
                  <Card className="p-3 bg-slate-700 border-slate-600 text-center">
                    <p className="text-2xl font-bold">{customerDetail.stats.daily_records_count}</p>
                    <p className="text-xs text-slate-400">Daily Records</p>
                  </Card>
                  <Card className="p-3 bg-slate-700 border-slate-600 text-center">
                    <p className="text-2xl font-bold">{customerDetail.stats.customers_count}</p>
                    <p className="text-xs text-slate-400">Customers</p>
                  </Card>
                  <Card className="p-3 bg-slate-700 border-slate-600 text-center">
                    <p className="text-2xl font-bold">{customerDetail.stats.alerts_count}</p>
                    <p className="text-xs text-slate-400">Alerts</p>
                  </Card>
                </div>

                {/* Users */}
                <div>
                  <h4 className="font-semibold mb-2">Staff Users</h4>
                  <div className="space-y-2">
                    {customerDetail.users.map(user => (
                      <div key={user.id} className="flex justify-between items-center p-2 bg-slate-700 rounded">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-xs text-slate-400">{user.mobile}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.role === 'admin' ? 'bg-amber-500/20 text-amber-400' :
                          user.role === 'manager' ? 'bg-blue-500/20 text-blue-400' :
                          user.role === 'standard' ? 'bg-green-500/20 text-green-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {user.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Plan Dialog */}
        <Dialog open={planDialog} onOpenChange={setPlanDialog}>
          <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
              <DialogDescription className="text-slate-400">
                Configure subscription plan details and features.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSavePlan} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Plan Name *</label>
                  <Input 
                    value={planForm.name}
                    onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                    placeholder="e.g., Monthly Pro"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Price (₹) *</label>
                  <Input 
                    type="number"
                    value={planForm.price}
                    onChange={(e) => setPlanForm({...planForm, price: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                    placeholder="999"
                    required
                    min={0}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Description</label>
                <Input 
                  value={planForm.description}
                  onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                  placeholder="Perfect for small fuel stations"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Duration (months)</label>
                  <Select 
                    value={planForm.duration_months.toString()} 
                    onValueChange={(v) => setPlanForm({...planForm, duration_months: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Month</SelectItem>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">12 Months (Annual)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={planForm.is_active}
                      onChange={(e) => setPlanForm({...planForm, is_active: e.target.checked})}
                      className="rounded"
                    />
                    Active
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={planForm.is_popular}
                      onChange={(e) => setPlanForm({...planForm, is_popular: e.target.checked})}
                      className="rounded"
                    />
                    Popular
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Features</label>
                {planForm.features.map((feature, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input 
                      value={feature}
                      onChange={(e) => {
                        const newFeatures = [...planForm.features];
                        newFeatures[idx] = e.target.value;
                        setPlanForm({...planForm, features: newFeatures});
                      }}
                      className="bg-slate-700 border-slate-600"
                      placeholder="Feature description"
                    />
                    {idx === planForm.features.length - 1 && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPlanForm({...planForm, features: [...planForm.features, '']})}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPlanDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-amber-500 to-orange-500">
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Offer Dialog */}
        <Dialog open={offerDialog} onOpenChange={setOfferDialog}>
          <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Create Offer</DialogTitle>
              <DialogDescription className="text-slate-400">
                Configure promotional discounts for subscription plans.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveOffer} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Offer Name *</label>
                <Input 
                  value={offerForm.name}
                  onChange={(e) => setOfferForm({...offerForm, name: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                  placeholder="e.g., Diwali Special"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Description</label>
                <Input 
                  value={offerForm.description}
                  onChange={(e) => setOfferForm({...offerForm, description: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                  placeholder="Get 20% off on annual plans"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Discount Type</label>
                  <Select 
                    value={offerForm.discount_type} 
                    onValueChange={(v) => setOfferForm({...offerForm, discount_type: v})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">
                    Discount Value {offerForm.discount_type === 'percentage' ? '(%)' : '(₹)'}
                  </label>
                  <Input 
                    type="number"
                    value={offerForm.discount_value}
                    onChange={(e) => setOfferForm({...offerForm, discount_value: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                    placeholder={offerForm.discount_type === 'percentage' ? '20' : '500'}
                    required
                    min={0}
                    max={offerForm.discount_type === 'percentage' ? 100 : undefined}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Valid From</label>
                  <Input 
                    type="date"
                    value={offerForm.valid_from}
                    onChange={(e) => setOfferForm({...offerForm, valid_from: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Valid Until</label>
                  <Input 
                    type="date"
                    value={offerForm.valid_until}
                    onChange={(e) => setOfferForm({...offerForm, valid_until: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={offerForm.is_active}
                  onChange={(e) => setOfferForm({...offerForm, is_active: e.target.checked})}
                  className="rounded"
                  id="offer-active"
                />
                <label htmlFor="offer-active" className="text-sm">Active (visible to customers)</label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOfferDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-green-500 to-emerald-500">
                  Create Offer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

export default MasterAdminDashboard;
